import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { DatePicker } from 'components/DatePicker';
import { cn } from 'lib/utils';
import { PhoneInput } from 'components/ui/phone-input';
import { UserAttributeData } from 'services/profile/type';
import { PATH_ROUTES } from 'routes/path';
import useUpdateProfile from 'hooks/useUpdateProfile';
import useAuthStore from 'store/auth';
import {
  AddMemberFormData,
  AddMemberValidator,
} from 'lib/validators/add-member';
import { NUMERIC_REGEX } from 'lib/constants';
import { usePostFamilyMemberAttributes } from 'services/profile/hooks';
import Confetti from 'components/Confetti';
import { captureUserActivity } from 'services/profile/api';
import { USER_ACTIVITY } from '../../types';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';

export default function AddNewMember() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo') || '';

  const familyMembers = useAuthStore((store) => store.memberDetails);
  const adminDetails = useAuthStore((store) => store.adminDetails);

  const { onUpdateProfile, onUpdateMemberDetails } = useUpdateProfile();

  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usePrimaryEmail, setUsePrimaryEmail] = useState<boolean>(false);
  const [usePrimaryPhone, setUsePrimaryPhone] = useState<boolean>(false);

  const form = useForm<AddMemberFormData>({
    resolver: zodResolver(AddMemberValidator),
    defaultValues: {
      birthdate: undefined,
      gender: '',
      email: '',
      phone_number: '',
      given_name: '',
      family_name: '',
      height: '',
      weight: '',
      height_unit: 'cm',
      weight_unit: 'kg',
    },
  });

  const { mutate } = usePostFamilyMemberAttributes({
    onSuccess: async (data) => {
      if (familyMembers.length === 1) {
        setIsLoading(false);
        form.reset();
        setShowConfetti(true);
        toast(t('congratulations'), {
          icon: '🎉',
          duration: 2000,
        });
      }

      const { profile_id } = data;
      if (profile_id) {
        captureUserActivity(USER_ACTIVITY.ADDING_PROFILE);
        await onUpdateMemberDetails();
        await onUpdateProfile(profile_id);
        navigate(redirectTo || PATH_ROUTES.DASHBOARD);
      } else {
        setShowConfetti(false);
      }
      setIsLoading(false);
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        const data = err.response?.data;
        if (data.email_duplication) {
          form.setError('email', {
            type: 'manual',
            message: t('email_already_in_use'),
          });
        }
        if (data.phone_duplication) {
          form.setError('phone_number', {
            type: 'manual',
            message: t('phone_number_already_in_use'),
          });
        }
      }
      setIsLoading(false);
    },
  });

  function onSubmit(values: AddMemberFormData) {
    setIsLoading(true);
    const data = {
      ...values,
      birthdate: format(values.birthdate, 'dd/MM/yyyy'),
      middle_name: '',
    };
    mutate(data as UserAttributeData);
  }

  const onPrimaryEmailUse = async (checked: boolean) => {
    setUsePrimaryEmail(checked);
    form.clearErrors('email');
    if (checked && adminDetails?.email) {
      form.setValue('email', adminDetails.email, { shouldDirty: true });
    } else {
      form.setValue('email', '');
    }

    await form.trigger('email');
  };

  const onPrimaryPhoneUse = async (checked: boolean) => {
    setUsePrimaryPhone(checked);
    form.clearErrors('phone_number');
    if (checked && adminDetails?.phone_number) {
      form.setValue('phone_number', adminDetails.phone_number, {
        shouldDirty: true,
      });
    } else {
      form.setValue('phone_number', '');
    }

    await form.trigger('phone_number');
  };

  return (
    <div className='h-fit py-8 sm:h-[calc(100vh-6rem)]'>
      <Confetti show={showConfetti} />

      <div className='flex items-center justify-center w-full h-full'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full max-w-2xl px-6 mx-auto space-y-6'
          >
            <div className='flex flex-col items-center w-full gap-8 sm:flex-row'>
              <FormField
                control={form.control}
                name='given_name'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('firstname')}</FormLabel>

                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='family_name'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('lastname')}</FormLabel>

                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className='flex flex-col items-center w-full gap-8 sm:flex-row'>
              <div className='flex flex-col w-full space-y-1'>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('email')}</FormLabel>

                      <FormControl>
                        <Input {...field} disabled={usePrimaryEmail} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className='flex items-center gap-x-2'>
                  <Checkbox
                    id='primary-email'
                    checked={usePrimaryEmail}
                    onCheckedChange={onPrimaryEmailUse}
                  />
                  <Label
                    htmlFor='primary-email'
                    className='mt-1 text-sm leading-none text-gray-900'
                  >
                    {t('use_primary_email')}
                  </Label>
                </div>
              </div>

              <div className='flex flex-col w-full space-y-1'>
                <FormField
                  control={form.control}
                  name='phone_number'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('phone')}</FormLabel>
                      <FormControl>
                        <PhoneInput
                          defaultCountry='MX'
                          {...field}
                          disabled={usePrimaryPhone}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className='flex items-center gap-x-2'>
                  <Checkbox
                    id='primary-phone'
                    checked={usePrimaryPhone}
                    onCheckedChange={onPrimaryPhoneUse}
                  />
                  <Label
                    htmlFor='primary-phone'
                    className='mt-1 text-sm leading-none text-gray-900'
                  >
                    {t('use_primary_phone')}
                  </Label>
                </div>
              </div>
            </div>

            <div className='flex flex-col items-center gap-8 sm:flex-row'>
              <FormField
                control={form.control}
                name='birthdate'
                render={({ field }) => (
                  <FormItem className='flex flex-col w-full'>
                    <FormLabel htmlFor='dob'>{t('dob')}</FormLabel>

                    <FormControl>
                      <DatePicker
                        selected={field.value}
                        onSelect={(value: Date | undefined) =>
                          field.onChange(value)
                        }
                      >
                        <Button
                          id='dob'
                          variant='ghost'
                          className={cn(
                            'justify-start hover:bg-transparent border-b-2 border-b-primary rounded-none h-12 pb-0 relative',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value
                            ? format(field.value, 'dd/MM/yyyy')
                            : null}
                          <CalendarIcon className='absolute w-5 h-5 right-2' />
                        </Button>
                      </DatePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='gender'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel htmlFor='gender'>{t('gender')}</FormLabel>

                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id='gender'
                          className='border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='male'>{t('male')}</SelectItem>
                          <SelectItem value='female'>{t('female')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className='grid items-center w-full grid-cols-1 gap-8 sm:grid-cols-2 sm:flex-row'>
              <div className='flex items-end'>
                <FormField
                  control={form.control}
                  name='height'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('height')}</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            const { value } = e.target;
                            const regex = NUMERIC_REGEX;
                            if (regex.test(value)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='height_unit'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          {...field}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className='w-20 border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='cm'>cm</SelectItem>
                            <SelectItem value='ft'>ft</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='flex items-end'>
                <FormField
                  control={form.control}
                  name='weight'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('weight')}</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            const { value } = e.target;
                            const regex = NUMERIC_REGEX;
                            if (regex.test(value)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='weight_unit'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          {...field}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className='w-20 border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='kg'>kg</SelectItem>
                            <SelectItem value='lbs'>lbs</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className='flex justify-center !mt-10 gap-4'>
              <Button
                className='px-6 rounded-full bg-rose-400 hover:bg-rose-400/90'
                onClick={() => navigate(-1)}
                type='button'
              >
                {t('cancel')}
              </Button>
              <Button
                type='submit'
                className='px-10 font-semibold rounded-full'
                disabled={!form.formState.isValid || isLoading}
              >
                {isLoading && (
                  <Loader2
                    role='progressbar'
                    className='inline w-4 h-4 mr-2 animate-spin'
                  />
                )}
                {t('add')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
